import {useContext, useMemo} from 'react';
import {PartnerKioskUpdate_UserSessionUpdateData} from '../../generated/partner_service_pb';
import {PlayerStateUpdate} from '../../generated/bay_session_pb';
import {StoreContext} from '../components/StoreContextProvider';
import {playerStateToUserSessionData} from '../utils/playerStateToUserSessionData';
import useStreamKioskUpdates, {LegacyConfirmationEvent} from './useStreamKioskUpdates';
import useStreamPlayerState from './useStreamPlayerState';

// useLiveSessionStream — the dual-backend live-session facade.
//
// One hook the UI consumes regardless of which backend authenticated the
// session. It dispatches on Context.sessionBackend and normalizes both backends
// to the canonical UI shape (PartnerKioskUpdate_UserSessionUpdateData):
//   - 'bay-session' → useStreamPlayerState(), projecting each PlayerState
//     through playerStateToUserSessionData().
//   - 'legacy'      → useStreamKioskUpdates(), which emits the canonical shape
//     natively (passthrough).
// Exactly one underlying stream opens for the active backend.

type Props = { enabled: boolean };

export type LiveSessionStream = {
  // Latest frame, normalized to the canonical UI shape (undefined before the
  // first frame). Identical across both backends — drives the common UI.
  update: PartnerKioskUpdate_UserSessionUpdateData | undefined;
  // The raw bay-session PlayerState frame, exposed as an escape hatch for the
  // handful of behaviors Main derives from fields the canonical shape can't
  // carry (per-user funds, activeBetId, currentStationId, awaiting-confirmation,
  // on_turn, autoplay/free-shot/skill-index, reason). undefined on legacy, which
  // does not carry these — consumers must treat undefined as "unavailable on
  // this backend" and degrade gracefully, never assume it is present.
  bayPlayerState: PlayerStateUpdate | undefined;
  legacyConfirmationEvent: LegacyConfirmationEvent | undefined;
  isConnected: boolean;
  streamError: string | null;
};

const useLiveSessionStream = ({enabled}: Props): LiveSessionStream => {
  const {
    sessionBackend, bayPlayerJwt, bayPlayerCredentialStatus, bayPlayerProjectionRetryKey
  } = useContext(StoreContext);
  const isBaySession = sessionBackend === 'bay-session';

  // Both hooks are called unconditionally (rules of hooks); only the active
  // backend's hook is enabled, so exactly one stream opens and a backend switch
  // closes the previous one before opening the next.
  //
  // The bay path is also gated on a non-empty bayPlayerJwt: the default backend
  // is 'bay-session', but a consumer login (email/OTP/image) sets `session`
  // without a JWT. Without this gate that session would enter useStreamPlayerState
  // with no JWT and spin its 5s reconnect loop forever. Gating on the JWT keeps
  // the inner hook torn down until a real bay credential exists.
  const playerState = useStreamPlayerState({
    enabled: enabled && isBaySession && bayPlayerCredentialStatus === 'ready' && !!bayPlayerJwt,
    retryKey: bayPlayerProjectionRetryKey
  });
  const kiosk = useStreamKioskUpdates({enabled: enabled && !isBaySession});

  // Project each PlayerState frame into the canonical shape. Memoized on the
  // raw frame reference so a steady-state re-render doesn't re-run the projection
  // or hand consumers a fresh reference for an unchanged frame.
  const projected = useMemo(
    () => playerStateToUserSessionData(playerState.update?.state),
    [playerState.update],
  );

  if (isBaySession) {
    return {
      update: projected, bayPlayerState: playerState.update ?? undefined, isConnected: playerState.isConnected,
      legacyConfirmationEvent: undefined, streamError: playerState.streamError
    };
  }

  // Legacy passthrough: the kiosk stream emits the canonical shape natively. No
  // raw bay frame exists here, so bayPlayerState is undefined and bay-only
  // behaviors degrade to no-ops.
  return {
    update: kiosk.update, bayPlayerState: undefined, legacyConfirmationEvent: kiosk.legacyConfirmationEvent,
    isConnected: kiosk.isConnected, streamError: kiosk.streamError
  };
};

export default useLiveSessionStream;
